import { useState, useEffect, useCallback, forwardRef } from 'react'
import { useRouter } from 'next/router'
import * as Yup from 'yup'
import 'yup-phone'

import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from '../../store'

import { useFormik } from 'formik'
import {
    Box,
    Button,
    Checkbox,
    FormHelperText,
    TextField,
    Typography,
    Link,
    Alert,
    AlertTitle,
    OutlinedInput,
    FormControl,
    experimentalStyled,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment
} from '@mui/material'
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input'

import { useAuth } from '../../hooks/use-auth'
import { useMounted } from '../../hooks/use-mounted'
import { gtm } from '../../lib/gtm'
import SavingBar from '../../components/saving-bar'
import selectorSettings from '../../store/selectors/settings'
import PhoneNumber from '../widgets/PhoneNumber'
import AcceptanceTermsCheckbox from './acceptance-terms-checkbox'

import Divider from '@mui/material/Divider'
import ReCAPTCHA from 'react-google-recaptcha'
import { useCookies } from 'react-cookie'

import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha
} from 'react-google-recaptcha-v3'

/*
Frontend:
<script>
  function setCampaignCookie() {
      if (window.location.search.match(/utm_campaign/i) == null) return false;

      setCookie('rads_marketing_ref', window.location.search, 60)
  }

  function setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays*24*60*60*1000));
    let expires = "expires="+ d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";domain="+ window.location.host.replace('www.', '.') +";path=/";
  }

  setCampaignCookie();
</script>

*/

export const JWTRegister = (props) => {
    const [cookies, setCookie] = useCookies(['cmpmrf', 'rads_marketing_ref'])

    const { t } = useTranslation()
    const isMounted = useMounted()
    const router = useRouter()
    const { register } = useAuth()

    const { executeRecaptcha } = useGoogleReCaptcha()

    const defaultCountry = selectorSettings.getDefaultCountry()
    const availableCountries = selectorSettings.getAvilableFormCountries()
    const userProfileCountries = selectorSettings.getUserProfileCountries()

    const formik = useFormik({
        initialValues: {
            first_name: router.query.hasOwnProperty('usr_first_name')
                ? router.query.usr_first_name
                : '',
            last_name: router.query.hasOwnProperty('usr_last_name')
                ? router.query.usr_last_name
                : '',
            email: router.query.hasOwnProperty('usr_email')
                ? router.query.usr_email
                : '',
            phone_number: '',
            password: '',
            password_re: '',
            company_name: router.query.hasOwnProperty('usr_company_name')
                ? router.query.usr_company_name
                : '',
            company_url: '',
            policy: false,
            version_tag: '',
            submit: null,
            token: null,
            country_code: defaultCountry ? defaultCountry.code : 'US',

            reg_step_1: false,
            reg_step_2: false,
            reg_step_3: false,
            reg_step_4: false
        },

        initialStatus: {
            success: false
        },
        //(479) 883-2288
        //704-467-0795
        validationSchema: Yup.object({
            first_name: Yup.string().max(50).required('Field is required'),
            last_name: Yup.string().max(50).required('Field is required'),
            company_name: Yup.string().max(80).required('Field is required'),
            // token: Yup.string().max(255).required("Google Recaptcha Required"),

            email: Yup.string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required'),

            phone_number: Yup.string()
                .required('Cell Phone Number is required')
                .test(
                    'phone-validation',
                    'Please provide a valid phone number.',
                    function (value) {
                        if (!value) return false

                        if (!value.startsWith('+')) return false

                        const digitsOnly = value.replace(/\D/g, '')

                        if (digitsOnly.length < 8) return false

                        const countryCode = this.parent.country_code

                        if (!countryCode) return false

                        try {
                            const phoneSchema = Yup.string().phone(
                                countryCode,
                                true
                            )

                            phoneSchema.validateSync(value)

                            return true
                        } catch (error) {
                            try {
                                const phoneSchemaLoose = Yup.string().phone(
                                    countryCode,
                                    false
                                )
                                phoneSchemaLoose.validateSync(value)
                                return true
                            } catch (error2) {
                                return false
                            }
                        }
                    }
                ),

            password: Yup.string()
                .min(7, 'Must be more than 7 characters')
                .max(255)
                .required('Password is required'),
            password_re: Yup.string()
                .min(7, 'Must be more than 7 characters')
                .max(255)
                .required('Password is required')
                .oneOf([Yup.ref('password'), null], 'Passwords must match'),

            policy: Yup.boolean().oneOf(
                [true],
                'You need to approve Terms and Conditions'
            ),
            version_tag: Yup.string().required('Terms version is required')
        }),
        onSubmit: async (values, helpers) => {
            try {
                const token = await handleReCaptchaVerify()

                const invite_ref_id = router.query.r

                if (!token) {
                    helpers.setStatus({ success: false })
                    helpers.setErrors({
                        formError:
                            'Unexpected Google ReCaptcha. Please reload your page and try again.'
                    })
                    helpers.setSubmitting(false)
                    return
                }

                let reg_cookie = cookies['rads_marketing_ref']
                    ? cookies['rads_marketing_ref']
                    : null
                reg_cookie = cookies['cmpmrf'] ? cookies['cmpmrf'] : null

                const { errorMessage, user } = await register(
                    values.first_name,
                    values.last_name,
                    values.phone_number,
                    values.email,
                    values.password,
                    values.company_name,
                    values.company_url,
                    token,
                    reg_cookie,
                    invite_ref_id,
                    values.country_code,
                    values.version_tag
                )

                if (isMounted() && user) {
                    gtm.push({
                        event: 'userinfo',
                        event_action: 'update',
                        first_name: values.first_name,
                        last_name: values.last_name,
                        email: values.email,
                        phone_number: values.phone_number,
                        company_name: values.company_name,
                        company_url: values.company_url
                    })

                    sessionStorage.setItem('username', values.email)

                    gtm.push({
                        event: 'rads_event',
                        event_category: 'user',
                        event_action: 'onAccountRegister',
                        event_label: 'success'
                    })

                    router.push('/authentication/verify-code.html')
                } else {
                    helpers.setStatus({ success: false })
                    helpers.setErrors({ formError: errorMessage })
                    helpers.setSubmitting(false)
                }
            } catch (err) {
                console.error(err)

                if (isMounted()) {
                    helpers.setStatus({ success: false })
                    helpers.setErrors({ formError: err.message })
                    helpers.setSubmitting(false)
                }
            }
        }
    })

    const selectedCountry = availableCountries.find(
        (availableCountry) =>
            availableCountry.code == formik.values.country_code
    )

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return null
        }
        const token = await executeRecaptcha('signup')
        // Do whatever you want with the token
        formik.setFieldValue('token', token)

        return token
    }, [executeRecaptcha, isMounted])

    // You can use useEffect to trigger the verification as soon as the component being loaded
    useEffect(() => {
        handleReCaptchaVerify()
    }, [handleReCaptchaVerify])

    const handleCountryChange = (event) => {
        const newCountryCode = event.target.value

        const dialCodeMap = {
            US: '+1',
            GB: '+44',
            CH: '+41'
        }

        const dialCode = dialCodeMap[newCountryCode] || ''

        formik.setFieldValue('country_code', newCountryCode)
        formik.setFieldValue('phone_number', dialCode)
    }

    if (formik.status.success) {
        return (
            <Box sx={{ mb: 2 }}>
                <Alert severity="success">
                    <AlertTitle>Account created!</AlertTitle>
                    <div>Please check your email to verify your account.</div>
                </Alert>
            </Box>
        )
    }
    return (
        <form noValidate onSubmit={formik.handleSubmit} {...props}>
            <SavingBar show={formik.isSubmitting} />

            {formik.errors.formError && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="error">
                        <div>{formik.errors.formError}</div>
                    </Alert>
                </Box>
            )}

            <Divider style={{ marginTop: 32 }}>Contact Details</Divider>

            <TextField
                error={Boolean(
                    formik.touched.first_name && formik.errors.first_name
                )}
                fullWidth
                required
                autoComplete="given-name"
                helperText={
                    formik.touched.first_name && formik.errors.first_name
                }
                label="First Name"
                margin="normal"
                name="first_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.first_name}
            />
            <TextField
                error={Boolean(
                    formik.touched.last_name && formik.errors.last_name
                )}
                fullWidth
                required
                autoComplete="family-name"
                helperText={formik.touched.last_name && formik.errors.last_name}
                label="Last Name"
                margin="normal"
                name="last_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.last_name}
            />
            <TextField
                error={Boolean(formik.touched.email && formik.errors.email)}
                fullWidth
                required
                autoComplete="email"
                helperText={formik.touched.email && formik.errors.email}
                label="Email Address"
                margin="normal"
                name="email"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="email"
                value={formik.values.email}
            />

            {availableCountries.length > 1 && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="demo-simple-select-label">
                        Target Country
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={formik.values.country_code}
                        label="Target Country"
                        name="country_code"
                        onBlur={formik.handleBlur}
                        onChange={handleCountryChange}
                    >
                        {availableCountries.map((availableCountry) => {
                            return (
                                <MenuItem
                                    key={`country_code_${availableCountry.name}`}
                                    value={availableCountry.code}
                                >
                                    {availableCountry.name}
                                </MenuItem>
                            )
                        })}
                    </Select>
                </FormControl>
            )}

            <PhoneNumber
                label="Phone Number"
                selectedCountry={formik.values.country_code.toLocaleLowerCase()}
                defaultCountry={formik.values.country_code.toLocaleLowerCase()}
                value={formik.values.phone_number}
                onBlur={formik.handleBlur}
                name="phone_number"
                onChange={(newPhoneNumber) =>
                    formik.setFieldValue('phone_number', newPhoneNumber)
                }
                onCountryChange={(newCountry) =>
                    formik.setFieldValue(
                        'country_code',
                        newCountry.toUpperCase()
                    )
                }
                error={Boolean(
                    formik.touched.phone_number && formik.errors.phone_number
                )}
                helperText={
                    formik.touched.phone_number && formik.errors.phone_number
                }
                sx={{ mt: 3 }}
            />

            <Divider style={{ marginTop: 32 }}>Company or Organization</Divider>

            <TextField
                error={Boolean(
                    formik.touched.company_name && formik.errors.company_name
                )}
                fullWidth
                required
                autoComplete="organization"
                helperText={
                    formik.touched.company_name && formik.errors.company_name
                }
                label="Company or Organization Name"
                margin="normal"
                name="company_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="text"
                value={formik.values.company_name}
            />

            <TextField
                error={Boolean(
                    formik.touched.company_url && formik.errors.company_url
                )}
                fullWidth
                autoComplete="url"
                helperText={
                    formik.touched.company_url && formik.errors.company_url
                }
                label="Company or Organization URL"
                margin="normal"
                name="company_url"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="text"
                value={formik.values.company_url}
            />

            <Divider style={{ marginTop: 32 }}>Password</Divider>

            <TextField
                error={Boolean(
                    formik.touched.password && formik.errors.password
                )}
                fullWidth
                required
                helperText={formik.touched.password && formik.errors.password}
                label="Password"
                margin="normal"
                name="password"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="password"
                value={formik.values.password}
            />

            <TextField
                error={Boolean(
                    formik.touched.password_re && formik.errors.password_re
                )}
                helperText={
                    formik.touched.password_re && formik.errors.password_re
                }
                fullWidth
                required
                label="Repeat Password"
                margin="normal"
                name="password_re"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="password"
                value={formik.values.password_re}
            />

            <Divider style={{ marginTop: 32 }}>Registration steps</Divider>

            <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                Please tick the checkboxes to confirm the registration process
            </Alert>

            <Box sx={{ alignItems: 'center', display: 'flex', ml: -1, mt: 2 }}>
                <Checkbox
                    checked={formik.values.reg_step_1}
                    name="reg_step_1"
                    onChange={formik.handleChange}
                />
                <Typography color="textSecondary" variant="body2">
                    <strong>Step 1</strong> - To verify your account, an email
                    will be sent to the address you provide. Please remember to
                    check your spam folder in case the email gets filtered
                    there.
                </Typography>
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', ml: -1, mt: 2 }}>
                <Checkbox
                    checked={formik.values.reg_step_2}
                    name="reg_step_2"
                    onChange={formik.handleChange}
                />
                <Typography color="textSecondary" variant="body2">
                    <strong>Step 2</strong> - Before you can access all the
                    features available on the platform, our team will review and
                    approve your account. Reviews usually take less than 24
                    hours.
                </Typography>
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', ml: -1, mt: 2 }}>
                <Checkbox
                    disabled
                    checked
                    name="reg_step_3"
                    onChange={formik.handleChange}
                />
                <Typography color="textSecondary" variant="body2">
                    <strong>Step 3</strong> - Once your account is approved,
                    you&apos;ll receive an email with the confirmation.
                </Typography>
            </Box>

            <Divider style={{ marginTop: 32 }}></Divider>

            <AcceptanceTermsCheckbox
                value={formik.values.policy}
                onChange={async (hasAccepted, termsVersion) => {
                    await formik.setValues(
                        {
                            ...formik.values,
                            policy: hasAccepted,
                            version_tag: termsVersion
                        },
                        true
                    )
                    formik.setFieldTouched('policy', true, false)
                }}
            />
            {Boolean(
                formik.touched.policy &&
                    !formik.values.policy &&
                    formik.errors.policy
            ) && (
                <FormHelperText error sx={{ ml: 2, mt: 1 }}>
                    {formik.errors.policy}
                </FormHelperText>
            )}

            {formik.errors.submit && (
                <Box sx={{ mt: 3 }}>
                    <FormHelperText error>
                        {formik.errors.submit}
                    </FormHelperText>
                </Box>
            )}
            <Box sx={{ mt: 2 }}>
                <Button
                    disabled={
                        formik.isSubmitting ||
                        !formik.isValid ||
                        !formik.values.policy
                    }
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                >
                    Register
                </Button>
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', mt: 4 }}>
                <Typography color="textSecondary" variant="body2">
                    <strong>Need Help?</strong>
                    <br />
                    If you have any questions or need assistance, please feel
                    free to reach out to{' '}
                    <a href={`mailto:${t('support_contact_email')}`}>
                        {t('support_contact_email')}
                    </a>
                    . We will be happy to help and provide the necessary
                    support.
                </Typography>
            </Box>
        </form>
    )
}
